import { z } from "zod";
import { enforceSameTenant, enforceVisibilityInvariant, nonEmptyStringSchema, sha256Schema, tenantOwnedShape, visibilityShape, } from "./common.js";
import { sourceItemRefSchema } from "./refs.js";
import { processingStateSchema } from "./vocabulary.js";
/**
 * Media understanding slice — frozen runtime contracts.
 *
 * Two contracts live here so the OCR sidecar (`apps/media-ocr`), the pipeline
 * client (`apps/workers`) and the persistence layer all resolve one source of
 * truth, per the domain being the center of runtime contracts.
 *
 * Sözleşme 1 — the OCR sidecar HTTP wire shape.
 * Sözleşme 2 — the persisted derived-layer record. It is NOT canonical: OCR
 * output is a confidence-scored derived interpretation and can never overwrite
 * canonical Markdown. No raw media bytes appear in either contract — media is
 * processed in-flight and never stored.
 */
// --- Sözleşme 1: OCR sidecar wire contract ------------------------------------
export const ocrTiers = ["gate", "quality"];
export const ocrTierSchema = z.enum(ocrTiers);
export const ocrExtractHints = ["auto", "document", "image"];
export const ocrExtractHintSchema = z.enum(ocrExtractHints);
/**
 * JSON-validatable request metadata. The image/PDF bytes travel as the binary
 * request body (multipart or octet-stream), never inside this object — the
 * sidecar does not persist them either.
 */
export const ocrExtractRequestMetaSchema = z
    .object({
    mediaType: nonEmptyStringSchema,
    hint: ocrExtractHintSchema.default("auto"),
})
    .strict();
export const ocrTableSchema = z
    .object({
    format: z.literal("html"),
    content: z.string(),
})
    .strict();
/**
 * Page texts are concatenated with this separator — skipping pages that carry
 * no text — to form the response `text`. The separator belongs to the contract
 * rather than to one engine: the pipeline reconstructs page boundaries from the
 * combined text, so a producer that joined differently would silently misplace
 * every boundary while still looking well-formed.
 */
export const OCR_PAGE_SEPARATOR = "\n\n";
/**
 * Hard ceiling on the page array. The sidecar's own page policy
 * (`MEDIA_OCR_MAX_PDF_PAGES`, itself capped at 50) sits below this, so a
 * response above this bound is malformed rather than merely large, and no
 * consumer has to size a buffer from an unbounded array.
 */
export const MAX_OCR_PAGES = 64;
/**
 * One rendered page of the source medium. A page that produced no text is kept
 * with an empty `text`: "page 3 yielded nothing" is an observation, and dropping
 * it would make a blank page indistinguishable from a page that was never read.
 */
export const ocrPageSchema = z
    .object({
    index: z
        .number()
        .int()
        .min(0)
        .max(MAX_OCR_PAGES - 1),
    text: z.string(),
    confidence: z.number().min(0).max(1),
    hasMeaningfulText: z.boolean(),
})
    .strict();
export const ocrExtractResponseSchema = z
    .object({
    tier: ocrTierSchema,
    engine: nonEmptyStringSchema,
    text: z.string(),
    confidence: z.number().min(0).max(1),
    hasMeaningfulText: z.boolean(),
    table: ocrTableSchema.nullable(),
    warnings: z.array(nonEmptyStringSchema).max(64),
    /**
     * A single score for the sidecar's own meaningfulness gate — the gate that
     * sets `hasMeaningfulText` false and therefore empties the text on the
     * pipeline side.
     *
     * The gate is not opaque today: the sidecar already publishes its measured
     * values and applied ceilings as warning tokens
     * (`ocr_measured_significant_characters=`, `ocr_applied_min_confidence=`
     * and their pair), and every withheld reading on staging carries them. What
     * they are not is queryable — reading them back means parsing `=`-delimited
     * strings out of an array. This field is where a sidecar may publish the
     * decision as a number instead; until one does, it is absent.
     *
     * Optional rather than defaulted so a producer that reports no score keeps
     * writing a valid response without naming the field at all.
     */
    meaningfulnessScore: z.number().min(0).max(1).nullable().optional(),
    /**
     * Additive and optional: an engine that reports no page structure still
     * returns a valid response, and its reading stays a single page-less span.
     */
    pages: z.array(ocrPageSchema).max(MAX_OCR_PAGES).default([]),
})
    .strict()
    .superRefine((value, context) => {
    if (value.hasMeaningfulText === false && value.table !== null) {
        context.addIssue({
            code: "custom",
            path: ["table"],
            message: "A table cannot be present when hasMeaningfulText is false.",
        });
    }
    if (value.pages.length === 0)
        return;
    for (const [position, page] of value.pages.entries()) {
        if (page.index !== position) {
            context.addIssue({
                code: "custom",
                path: ["pages", position, "index"],
                message: "Pages must be reported in order, indexed from zero.",
            });
            return;
        }
    }
    const combined = value.pages
        .filter((page) => page.text.length > 0)
        .map((page) => page.text)
        .join(OCR_PAGE_SEPARATOR);
    if (combined !== value.text) {
        context.addIssue({
            code: "custom",
            path: ["pages"],
            message: "Page texts must reconstruct the combined text exactly.",
        });
    }
});
/**
 * Sidecar rejections that come from a configured LIMIT rather than from the
 * content itself. A limit is an operator decision that can change, so a
 * rejection carrying one of these codes is never a final answer about the
 * media — it is `unsupported`, and the item stays eligible for a later pass.
 * A genuine content fault (a corrupt magic number, an engine contract
 * violation) is outside this vocabulary and stays terminally `failed`.
 */
export const ocrPolicyLimitSafeCodes = [
    "media_size_limit_exceeded",
    "media_pixel_limit_exceeded",
    "pdf_page_limit_exceeded",
    "pdf_raster_size_limit_exceeded",
    "ocr_output_limit_exceeded",
];
export const ocrPolicyLimitSafeCodeSchema = z.enum(ocrPolicyLimitSafeCodes);
export function isOcrPolicyLimitSafeCode(value) {
    return ocrPolicyLimitSafeCodeSchema.safeParse(value).success;
}
/** Wire identifier of the extract contract a sidecar build implements. */
export const OCR_CAPABILITIES_CONTRACT = "obsina-ocr-extract-v1";
/**
 * Bounds on what a sidecar may claim about itself. They live here rather than
 * on either side of the wire so the publisher and the reader cannot drift into
 * accepting different numbers for the same field.
 */
export const MAX_OCR_CONCURRENT_JOBS = 64;
export const MAX_OCR_RETRY_AFTER_MS = 600_000;
const ocrCapacityShape = {
    /** Whether a slot is free right now — deliberately not a health verdict. */
    acceptingWork: z.boolean(),
    activeJobs: z.number().int().min(0).max(MAX_OCR_CONCURRENT_JOBS),
    maxConcurrentJobs: z.number().int().min(1).max(MAX_OCR_CONCURRENT_JOBS),
    /** Advisory, and only present while work is being refused. */
    retryAfterMs: z.number().int().min(0).max(MAX_OCR_RETRY_AFTER_MS).nullable(),
};
/**
 * What `GET /v1/capabilities` publishes. `status` is the engine's *health* —
 * its bounded dependency self-test — while `acceptingWork` is live occupancy.
 * The two are separate answers: a saturated sidecar passes every self-test it
 * has, so a caller reading only health keeps dispatching work that can only be
 * shed, and a caller treating saturation as sickness closes a lane over what is
 * a queueing condition.
 */
export const ocrCapabilitiesResponseSchema = z
    .object({
    status: z.literal("ready"),
    contract: z.literal(OCR_CAPABILITIES_CONTRACT),
    ...ocrCapacityShape,
})
    .strict();
/**
 * The same contract as a *caller* must read it, across sidecar versions.
 *
 * Every field falls back on its own, never as a group: a body that says
 * `acceptingWork: false` next to one malformed sibling still means "refusing".
 * Dropping the whole object there would turn an explicit refusal into an
 * invitation — the one direction this contract must never fail in.
 *
 * The fallbacks themselves are the safe reading of silence. A sidecar that
 * reports no capacity is not evidence of saturation (defaulting to "refusing"
 * would idle a caller's lane after a rollback), and not evidence of headroom
 * either — so an unreported ceiling is credited with exactly one slot.
 */
export const ocrCapabilitiesObservationSchema = z.object({
    acceptingWork: ocrCapacityShape.acceptingWork.catch(true),
    maxConcurrentJobs: ocrCapacityShape.maxConcurrentJobs.catch(1),
    retryAfterMs: ocrCapacityShape.retryAfterMs.catch(null),
});
// --- Sözleşme 2: persisted derived-layer record -------------------------------
/** Bounds mirrored by the app_private.media_extraction CHECK constraints. */
export const MAX_WARNINGS = 64;
export const MAX_WARNING_CHARACTERS = 128;
export const MAX_FAILURE_CODE_CHARACTERS = 128;
export const mediaExtractionLayerKinds = ["ocr_derived"];
export const mediaExtractionLayerKindSchema = z.enum(mediaExtractionLayerKinds);
/**
 * Why the derived document layer refused to project an otherwise durable OCR
 * reading. The reading itself is always kept: a refused projection is an
 * explicit terminal outcome, never a silently dropped observation.
 */
export const mediaExtractionProjectionRejectionReasons = [
    "empty_text",
    "ineligible_extraction",
    "invalid_input",
    "low_confidence",
    "metadata_too_large",
    "scope_mismatch",
    "stale_source_hash",
    "text_too_large",
];
export const mediaExtractionProjectionRejectionReasonSchema = z.enum(mediaExtractionProjectionRejectionReasons);
/**
 * One text-bearing source page's structural footprint inside `extractedText`.
 * `sourcePageIndex` is the real zero-based index of the rendered source page —
 * so a blank interior page leaves a gap in the sequence — and `characterLength`
 * is that page's length in Unicode code points. It never carries the page text,
 * only its position and extent, so the derived layer can bind a block to
 * "source page 3" without re-splitting the combined text on a formatting
 * separator. The wire contract's `OcrPage.index` is the sole authority on the
 * real page index; this persists it so a lossy re-split never has to guess it.
 */
export const mediaTextPageSchema = z
    .object({
    sourcePageIndex: z
        .number()
        .int()
        .min(0)
        .max(MAX_OCR_PAGES - 1),
    characterLength: z.number().int().min(1),
})
    .strict();
const OCR_PAGE_SEPARATOR_CODE_POINTS = Array.from(OCR_PAGE_SEPARATOR).length;
export const mediaExtractionSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    sourceItemRef: sourceItemRefSchema,
    layerKind: mediaExtractionLayerKindSchema,
    engine: nonEmptyStringSchema,
    tier: ocrTierSchema,
    extractedText: z.string(),
    confidence: z.number().min(0).max(1),
    hasMeaningfulText: z.boolean(),
    tableHtml: z.string().nullable(),
    processingStatus: processingStateSchema,
    sourceHash: sha256Schema,
    /**
     * Additive and optional: a producer that knows nothing about projection
     * eligibility still writes a valid record.
     */
    projectionRejectionReason: mediaExtractionProjectionRejectionReasonSchema
        .nullable()
        .default(null),
    /**
     * The record must carry the reason for its own emptiness. Without these two
     * fields a row written by an unconfigured engine is bit-for-bit identical to
     * one written for a genuinely text-free image, and no operator can tell the
     * two apart or find the set to reprocess. Mirrors Sözleşme 1's `warnings`
     * and carries the redacted safe code of a failed attempt — never a raw
     * provider or engine error string.
     */
    warnings: z
        .array(nonEmptyStringSchema.max(MAX_WARNING_CHARACTERS))
        .max(MAX_WARNINGS)
        .default([]),
    failureCode: nonEmptyStringSchema
        .max(MAX_FAILURE_CODE_CHARACTERS)
        .nullable()
        .default(null),
    /**
     * The sidecar's own meaningfulness score, carried through from Sözleşme 1 so
     * the decision that emptied `extractedText` is queryable as a number rather
     * than reconstructed by parsing warning tokens. Null when the producer
     * reported none — silence, never zero.
     */
    meaningfulnessScore: z.number().min(0).max(1).nullable().default(null),
    /**
     * Additive and optional structural page map for `extractedText`. Each entry
     * is one text-bearing source page, in combined-text order, carrying its real
     * source page index and code-point length. Persisting it lets the derived
     * layer bind each block to the exact source page instead of re-deriving page
     * boundaries by splitting the combined text: a blank interior page no longer
     * shifts a later page's provenance, and a producer that emits a page-internal
     * blank line no longer forges an extra page. A producer that reports no page
     * structure leaves it null; the reading then stays a single page-less span.
     * Never carries page text.
     */
    textPages: z
        .array(mediaTextPageSchema)
        .max(MAX_OCR_PAGES)
        .nullable()
        .default(null),
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    // Mirror Sözleşme 1's wire invariant so a record re-parsed from the database
    // cannot claim a table while reporting no meaningful text; without this the
    // persisted schema accepts a semantically invalid row the wire schema rejects.
    if (value.hasMeaningfulText === false && value.tableHtml !== null) {
        context.addIssue({
            code: "custom",
            path: ["tableHtml"],
            message: "A table cannot be present when hasMeaningfulText is false.",
        });
    }
    if (value.projectionRejectionReason !== null &&
        value.processingStatus === "success") {
        context.addIssue({
            code: "custom",
            path: ["projectionRejectionReason"],
            message: "A successful extraction cannot carry a projection rejection.",
        });
    }
    if (value.textPages !== null) {
        let previousIndex = -1;
        let ordered = true;
        for (const [position, page] of value.textPages.entries()) {
            if (page.sourcePageIndex <= previousIndex) {
                context.addIssue({
                    code: "custom",
                    path: ["textPages", position, "sourcePageIndex"],
                    message: "Text pages must be ordered by strictly increasing source page index.",
                });
                ordered = false;
                break;
            }
            previousIndex = page.sourcePageIndex;
        }
        // The persisted structure must reconstruct the combined text exactly: the
        // page lengths plus the separators between them are the whole reading.
        if (ordered) {
            const declaredLength = value.textPages.reduce((total, page) => total + page.characterLength, 0) +
                OCR_PAGE_SEPARATOR_CODE_POINTS *
                    Math.max(0, value.textPages.length - 1);
            if (declaredLength !== Array.from(value.extractedText).length) {
                context.addIssue({
                    code: "custom",
                    path: ["textPages"],
                    message: "Text page lengths must reconstruct the combined text length exactly.",
                });
            }
        }
    }
    enforceSameTenant(value.tenantId, [
        {
            tenantId: value.sourceItemRef.tenantId,
            path: ["sourceItemRef", "tenantId"],
        },
    ], context);
});
//# sourceMappingURL=media-extraction.js.map